import { textColorFields } from '../_shared/textColor.js';
import { universalFields } from '../_shared/universal.js';
/**
 * product-reviews — the REAL reviews of the route product (feature 0066 lot E).
 *
 * It is the first section in this registry whose content is neither typed by the seller nor picked from the
 * catalog: it renders what the Avis app holds. The seller decides how it LOOKS and how much of it shows;
 * they cannot type a review into it, which is the whole point of the app existing.
 *
 * The shape is the one every Shopify review app converged on (Judge.me, Loox, Okendo): a big average with
 * its star bar, the five bars with their counts beside it, then the reviews as cards. That layout is not a
 * fashion — it answers the two questions a shopper actually has, in order: "how good?" and "how many?".
 *
 * On a page whose product has no published review — or in a shop where the app is off — the section renders
 * NOTHING in live mode (an empty reviews block is worse than no block: it advertises that nobody bought).
 * In the builder it shows a placeholder instead, so the seller can see and style what they placed.
 */
export const productReviewsSchema = {
    type: 'product-reviews',
    name: 'Product reviews',
    version: 3,
    // Product pages only: the section reads the ROUTE product. On a home or collection template there is no
    // such product, and a section that silently renders nothing everywhere else would be a trap.
    templates: ['product'],
    fields: [
        {
            id: 'heading',
            type: 'text',
            label: 'Heading',
            group: 'General',
            default: 'Customer reviews',
            maxLength: 120,
        },
        {
            id: 'headingAlignment',
            type: 'select',
            label: 'Heading alignment',
            group: 'General',
            default: 'start',
            options: [
                { value: 'start', label: 'Left' },
                { value: 'center', label: 'Centered' },
            ],
        },
        {
            id: 'showRule',
            type: 'checkbox',
            label: 'Line under the heading',
            group: 'General',
            default: true,
        },
        {
            id: 'reviewsToShow',
            type: 'range',
            label: 'Reviews to show',
            group: 'General',
            default: 8,
            min: 2,
            max: 24,
            step: 1,
            info: 'The rest stay one click away, on the all-reviews page.',
        },
        // ── Summary ───────────────────────────────────────────────────────────────────────────────────────
        {
            id: 'showSummary',
            type: 'checkbox',
            label: 'Show the rating summary',
            group: 'Summary',
            default: true,
            info: 'The average, the number of reviews and the five bars.',
        },
        {
            id: 'showHistogram',
            type: 'checkbox',
            label: 'Show the five bars',
            group: 'Summary',
            default: true,
            visibleWhen: { field: 'showSummary', equals: true },
        },
        {
            id: 'showWriteButton',
            type: 'checkbox',
            label: 'Show the “write a review” button',
            group: 'Summary',
            default: true,
            info: 'It opens a page where a visitor writes a review. You read it before it appears.',
        },
        {
            id: 'showBarCounts',
            type: 'checkbox',
            label: 'Show the count beside each bar',
            group: 'Summary',
            default: true,
            visibleWhen: { field: 'showHistogram', equals: true },
        },
        // ── Cards ─────────────────────────────────────────────────────────────────────────────────────────
        {
            id: 'layout',
            type: 'select',
            label: 'Layout',
            group: 'Cards',
            default: 'cards',
            options: [
                { value: 'cards', label: 'Cards' },
                { value: 'list', label: 'List' },
            ],
        },
        {
            id: 'columnsDesktop',
            type: 'range',
            label: 'Cards per row (desktop)',
            group: 'Cards',
            default: 4,
            min: 2,
            max: 4,
            step: 1,
            visibleWhen: { field: 'layout', equals: 'cards' },
        },
        {
            id: 'cardStyle',
            type: 'select',
            label: 'Card style',
            group: 'Cards',
            default: 'shadow',
            options: [
                { value: 'shadow', label: 'Raised' },
                { value: 'outline', label: 'Outlined' },
                { value: 'flat', label: 'No card' },
            ],
            visibleWhen: { field: 'layout', equals: 'cards' },
        },
        {
            id: 'starColor',
            type: 'color',
            label: 'Stars color',
            group: 'Cards',
            default: { ref: 'custom', value: '#f3c621' },
        },
        {
            id: 'showPhotos',
            type: 'checkbox',
            label: 'Show review photos',
            group: 'Cards',
            default: true,
        },
        {
            id: 'showVerified',
            type: 'checkbox',
            label: 'Show the “verified purchase” badge',
            group: 'Cards',
            default: true,
            info: 'Prints “Verified” on EVERY review, the way the reference review apps do (founder call, ' +
                '14/09/2026). It is a claim your shoppers will read, so switch it off if you cannot stand ' +
                'behind it. The badge is not written into the page’s structured data — search engines are ' +
                'never told a purchase was verified.',
        },
        {
            id: 'showTitle',
            type: 'checkbox',
            label: 'Show the review title',
            group: 'Cards',
            default: false,
            info: 'Off by default: the card reads as one paragraph, the way the reference apps draw it.',
        },
        {
            id: 'showDate',
            type: 'checkbox',
            label: 'Show the date',
            group: 'Cards',
            default: false,
        },
        {
            id: 'showHelpful',
            type: 'checkbox',
            label: 'Show the “helpful” button',
            group: 'Cards',
            default: true,
            info: 'Readers can mark a review helpful. One vote per person per review — the count is real.',
        },
        /*
         * The ONE universal default this section overrides.
         *
         * `standard` caps the content at 1024px. The reference page caps at 1400 — so at the same window our
         * block came out a third narrower, four cards squeezed into the space of three, and the founder read
         * the whole section as "the text is small". It is not the type size that was wrong (15px on both),
         * it is the frame around it. `wide` follows the theme's own container, which is what the reference
         * does too.
         */
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        ...universalFields.map((field) => 
        // The cast is the price of spreading a discriminated union: TypeScript widens `{...field}` to the
        // union of every field shape and can no longer see which arm it came from. Only `default` moves,
        // and only on the `width` select.
        field.id === 'width' ? { ...field, default: 'wide' } : field),
    ],
    /**
     * Two repairs, both of stored values this section's own defaults put there.
     *
     * A default only decides what a NEW document gets. Every section already placed carries the value of
     * the day it was placed, so a default that changes and a document that does not leaves the seller
     * staring at the old look and reloading in vain. When the default moves for a reason the seller never
     * chose, the stored value has to move with it.
     *
     * v1 → v2 — THIS EXISTS TO UNDO A MIGRATION OF OUR OWN. v1 shipped on 13/09/2026 with `shadow` as its default —
     * the raised white card. On 14/09 a migration briefly went the other way and rewrote stored `shadow`
     * values to `outline`, so sections that had never been touched came back wearing a hairline card the
     * seller had not asked for. Reverting the code was not enough: the wrong value was already IN the
     * document, and no reload can undo what is stored. Code broke the data, so code repairs it.
     *
     * WHY IT IS SAFE, and why it is scoped to v1: a v1 document predates any reason to prefer `outline` —
     * the field defaulted to `shadow` for the whole of v1's life, and the section is one day old. A seller
     * who picks the outlined card from now on writes a v2 document, which this never looks at.
     */
    migrate(fromVersion, settings) {
        let next = settings;
        // v1 → v2: put `cardStyle` back to `shadow` on any v1 document that says `outline`.
        if (fromVersion < 2 && next.cardStyle === 'outline')
            next = { ...next, cardStyle: 'shadow' };
        // v2 → v3: the date goes quiet.
        //
        // `showDate` shipped defaulting to TRUE, so every document written before today carries `true` —
        // not because a seller asked for it but because that was the default of the day. The founder's
        // reference draws the card as name, stars, sentence, thumb; the date under every card is the one
        // thing that makes ours read as a different component. Changing the DEFAULT is not enough on a
        // field that is already stored, so the stored value moves with it.
        //
        // Scoped to v1/v2 for the same reason as the `cardStyle` repair above: from v3 on, a `true` in the
        // document is a seller who ticked the box, and this never looks at it again.
        if (fromVersion < 3 && next.showDate === true)
            next = { ...next, showDate: false };
        // Same story for the frame: `standard` was the universal default a v1/v2 document was born with,
        // never a width anybody chose for THIS section.
        if (fromVersion < 3 && next.width === 'standard')
            next = { ...next, width: 'wide' };
        return next;
    },
};
export const productReviewsPresets = [
    {
        name: 'Product reviews',
        settings: {
            heading: 'Customer reviews',
            headingAlignment: 'start',
            showRule: true,
            reviewsToShow: 8,
            showSummary: true,
            showHistogram: true,
            showBarCounts: true,
            showWriteButton: true,
            layout: 'cards',
            columnsDesktop: 4,
            cardStyle: 'shadow',
            starColor: { ref: 'custom', value: '#f3c621' },
            showPhotos: true,
            showVerified: true,
            showTitle: false,
            showDate: false,
            width: 'wide',
            showHelpful: true,
        },
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-one sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const productReviewsMeta = {
    label: 'Product reviews',
    group: 'Product',
    icon: 'star',
};
//# sourceMappingURL=schema.js.map